import { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { getRotatedTaxiIcon, calculateBearing } from "../../../utils/taxi";

const ANIMATION_DURATION = 2500; // ms de duración para la interpolación entre saltos de coordenadas

export const AnimatedDriverMarker = ({ driver, onClick }) => {
  const map = useMap();
  const markerRef = useRef(null);
  
  // Referencias para rastrear el estado de la animación de interpolación
  const positionRef = useRef({ lat: driver.coords.lat, lng: driver.coords.lon });
  const headingRef = useRef(driver.heading || 0);
  const animationFrameId = useRef(null);
  const animationStartTime = useRef(null);
  const startPosition = useRef(null);
  const endPosition = useRef(null);

  // Inicializar y destruir el marcador nativo de Google Maps
  useEffect(() => {
    if (!map || !window.google) return;

    // Crear marcador nativo clásico
    const marker = new window.google.maps.Marker({
      map,
      position: { lat: driver.coords.lat, lng: driver.coords.lon },
      title: driver.name,
      zIndex: 100,
    });

    // Cargar ícono de taxi rotado inicial
    getRotatedTaxiIcon(driver.heading || 0, driver.inTrip).then((dataUrl) => {
      if (markerRef.current) {
        marker.setIcon({
          url: dataUrl,
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 16),
        });
      }
    });

    // Agregar evento de click nativo pasando la posición animada actual
    const clickListener = marker.addListener("click", () => {
      if (onClick) {
        onClick(positionRef.current || { lat: driver.coords.lat, lng: driver.coords.lon });
      }
    });

    markerRef.current = marker;

    return () => {
      // Limpieza al desmontar
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      clickListener.remove();
      marker.setMap(null);
      markerRef.current = null;
    };
  }, [map, driver.inTrip]);

  const prevInTrip = useRef(driver.inTrip);

  // Actualizar la posición y ejecutar la interpolación (Lerp) suave
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;

    const newLat = driver.coords.lat;
    const newLng = driver.coords.lon;

    const prevPosition = positionRef.current;
    const distanceMoved =
      Math.abs(newLat - prevPosition.lat) + Math.abs(newLng - prevPosition.lng);

    // Si la posición cambió (desplazamiento real)
    if (distanceMoved > 0.00001) {
      // 1. Calcular Rumbo (heading) basado en desplazamiento si viene vacío/0
      let computedHeading = driver.heading;
      if (!computedHeading || computedHeading === 0) {
        computedHeading = calculateBearing(
          { lat: prevPosition.lat, lon: prevPosition.lng },
          { lat: newLat, lon: newLng }
        );
      }
      headingRef.current = computedHeading;
      prevInTrip.current = driver.inTrip;

      // Actualizar ícono rotado según el nuevo rumbo e inTrip
      getRotatedTaxiIcon(computedHeading, driver.inTrip).then((dataUrl) => {
        if (markerRef.current) {
          markerRef.current.setIcon({
            url: dataUrl,
            scaledSize: new window.google.maps.Size(32, 32),
            anchor: new window.google.maps.Point(16, 16),
          });
        }
      });

      // 2. Iniciar la animación de interpolación suave (Lerp)
      startPosition.current = { ...prevPosition };
      endPosition.current = { lat: newLat, lng: newLng };
      animationStartTime.current = performance.now();

      const animateMarker = (time) => {
        if (!animationStartTime.current || !markerRef.current) return;

        const elapsed = time - animationStartTime.current;
        const progress = Math.min(elapsed / ANIMATION_DURATION, 1);

        // Interpolación lineal simple
        const currentLat =
          startPosition.current.lat +
          (endPosition.current.lat - startPosition.current.lat) * progress;
        const currentLng =
          startPosition.current.lng +
          (endPosition.current.lng - startPosition.current.lng) * progress;

        const newPos = new window.google.maps.LatLng(currentLat, currentLng);
        markerRef.current.setPosition(newPos);

        // Guardar la posición actual alcanzada
        positionRef.current = { lat: currentLat, lng: currentLng };

        if (progress < 1) {
          animationFrameId.current = requestAnimationFrame(animateMarker);
        }
      };

      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      animationFrameId.current = requestAnimationFrame(animateMarker);
    } else {
      // Si el conductor está estático pero actualiza su rumbo o estado de viaje (inTrip) directamente
      if (driver.heading !== headingRef.current || driver.inTrip !== prevInTrip.current) {
        headingRef.current = driver.heading || 0;
        prevInTrip.current = driver.inTrip;
        getRotatedTaxiIcon(driver.heading || 0, driver.inTrip).then((dataUrl) => {
          if (markerRef.current) {
            markerRef.current.setIcon({
              url: dataUrl,
              scaledSize: new window.google.maps.Size(32, 32),
              anchor: new window.google.maps.Point(16, 16),
            });
          }
        });
      }
    }
  }, [driver.coords.lat, driver.coords.lon, driver.heading, driver.inTrip]);

  return null; // El marcador se dibuja nativamente en el mapa, no renderiza nada en el DOM de React
};
